import type { $Fetch } from 'nitropack'
import type { NuxtAxiosInstance } from '@/types/axios'
import { getOrderStatus as adaptOrderStatus, getDeliveryOrderStatus } from '@/utils/status-manager'
import type { ResultGenericVM } from '@/view-models/common'
import type {
  OrderDetail,
  RescheduleParams,
  UpdateReschedulePayload,
  UpdateOrderParams,
  OrderSourceData,
  OrderDetailParams,
  OrderStatusParams,
  CancelOrderParams,
  OrderCancelInfoParams,
  OrderRescheduleInfoParams,
} from '@/view-models/order'
import type {
  FunnowOrderOrderStatusData,
  OrderCancelRuleRespData,
  OrderOrderIDData,
  OrderRescheduleRuleRespData,
  OrderRschTimeRespData,
  OrdersUpdateBookingTimeRes,
  OrderUpdateOrderByTokenReq as OrderUpdateOrderData,
} from '@/types/api'

/**
 * Generate endpoint prefix based on token presence
 */
const genEndpointPrefix = (token?: string): string => (token ? `/pub/order` : '/order')

/**
 * 取得訂單 reschedule 時間
 */
export async function getReschedule(
  axios: NuxtAxiosInstance,
  { orderId, token, headcount }: RescheduleParams
): Promise<ResultGenericVM<Required<OrderRschTimeRespData>>> {
  const endpoint = genEndpointPrefix(token) + `/${orderId}/reschedule`
  const params = {
    ...(token ? { t: token } : {}),
    headcount,
  }
  const res = await axios.$get(endpoint, { params, headers: { APIVer: 2 } })

  if (res.code !== 0) {
    throw res
  }

  return res
}

/**
 * 更新訂單預訂時間
 */
export async function updateReschedule(
  axios: NuxtAxiosInstance,
  orderId: string | number,
  payload: UpdateReschedulePayload
): Promise<ResultGenericVM<Required<OrdersUpdateBookingTimeRes>>> {
  const endpoint = genEndpointPrefix(payload.token) + `/${orderId}/reschedule`
  const res = await axios.$put(endpoint, payload)

  if (res.code !== 0) {
    throw res
  }

  return res
}

/**
 * 查訂單的 Source 與 DomainPrefix
 */
export async function getOrderSource(axios: NuxtAxiosInstance, { orderID }: { orderID: string }): Promise<OrderSourceData | null> {
  try {
    const result = await axios.$get(`/order/${orderID}/source`)
    return result.data
  } catch (error) {
    console.error(error)
    return null
  }
}

/**
 * 取得訂單詳細資料
 */
export async function getOrderDetail(axios: NuxtAxiosInstance, { orderID, token }: OrderDetailParams): Promise<ResultGenericVM<OrderDetail>> {
  try {
    const endpoint = genEndpointPrefix(token) + `/${orderID}`
    const params = new URLSearchParams()
    if (token) {
      params.append('t', token)
    }
    const res = await axios.$get(endpoint, {
      params: Object.fromEntries(params.entries()),
      headers: { APIVer: 2 },
    })

    if (res.code === 0) {
      const orderStatus = res.data.delivery ? getDeliveryOrderStatus(res.data) : adaptOrderStatus(res.data)
      res.data = { ...res.data, order_status: orderStatus }
    }
    return res
  } catch (error) {
    console.error('getOrderDetail:error', error)
    return error as ResultGenericVM<OrderDetail>
  }
}

/**
 * 取得訂單狀態
 */
export async function getOrderStatus(axios: NuxtAxiosInstance, { orderID, token }: OrderStatusParams) {
  try {
    const endpoint = genEndpointPrefix(token) + `/${orderID}/status`
    const res = await axios.$get<ResultGenericVM<FunnowOrderOrderStatusData>>(endpoint, { params: token ? { t: token } : {} })
    return res
  } catch (error) {
    console.error('getOrderStatus:error', error)
    return error as ResultGenericVM<FunnowOrderOrderStatusData>
  }
}

/**
 * 建立訂單
 */
export async function createOrder(axios: NuxtAxiosInstance, params: any) {
  try {
    const res = await axios.$post<ResultGenericVM<OrderOrderIDData>>(`/order`, params)
    if (res.code !== 0) {
      throw res
    }
    return res
  } catch (error) {
    console.error(error)
    throw error
  }
}

/**
 * 更新訂單
 */
export async function updateOrder(axios: NuxtAxiosInstance, orderId: string | number, params: UpdateOrderParams) {
  const endpoint = genEndpointPrefix(params.token) + `/${orderId}`

  try {
    const res = await axios.$patch<ResultGenericVM<OrderUpdateOrderData>>(endpoint, params)
    if (res.code !== 0) {
      throw res
    }
    return res
  } catch (error) {
    console.error(error)
  }
}

/**
 * 取消訂單
 */
export async function cancelOrder(
  axios: NuxtAxiosInstance,
  orderId: string | number,
  params: CancelOrderParams = {}
): Promise<ResultGenericVM<unknown>> {
  try {
    const endpoint = genEndpointPrefix(params.token) + `/${orderId}/cancel`
    const res = await axios.$post(endpoint, { token: params.token })
    if (res.code !== 0) {
      throw res
    }
    return res
  } catch (error) {
    console.error(error)
    throw error
  }
}

/**
 * 取得訂單取消規則
 */
export async function getOrderCancelInfo(axios: NuxtAxiosInstance, { orderId, token }: OrderCancelInfoParams) {
  try {
    const endpoint = genEndpointPrefix(token) + `/${orderId}/cancel-rule`
    const res = await axios.$get<ResultGenericVM<OrderCancelRuleRespData>>(endpoint, { params: token ? { t: token } : {} })
    if (res.code !== 0) {
      throw res
    }
    return res
  } catch (error) {
    console.error('getOrderCancelInfo:error', error)
    throw error
  }
}

/**
 * 取得訂單更改時間規則
 */
export async function getOrderRescheduleInfo(axios: NuxtAxiosInstance, { orderId, token }: OrderRescheduleInfoParams) {
  try {
    const endpoint = genEndpointPrefix(token) + `/${orderId}/reschedule-rule`
    const res = await axios.$get<ResultGenericVM<Required<OrderRescheduleRuleRespData>>>(endpoint, { params: token ? { t: token } : {} })
    if (res.code !== 0) {
      throw res
    }
    return res
  } catch (error) {
    console.error('getOrderRescheduleInfo:error', error)
    throw error
  }
}

export async function updateBookingHolding(
  fetch: $Fetch & NuxtAxiosInstance,
  orderId: string | number,
  params?: { token?: string }
): Promise<boolean> {
  try {
    const endpoint = genEndpointPrefix(params?.token) + `/${orderId}/booking-hold`
    const res = await fetch<ResultGenericVM<boolean>>(endpoint, {
      method: 'POST',
      body: params,
    })
    if (res.code !== 0) {
      throw res
    }
    return true
  } catch (error) {
    console.error('updateBookingHolding:error', error)
    throw error
  }
}
